export const SCROLL_SPEED_PX_PER_S = 40;
export const SCROLL_DELAY_MS = 400;
export const MIN_SWEEP_MS = 600;
export const MAX_SWEEP_MS = 6000;
export const RETURN_MS = 250;
export const EDGE_FADE_PX = 12;

const SUB_PIXEL_NOISE_PX = 0.05;

export const sweepDurationMs = (distancePx: number): number =>
  Math.min(MAX_SWEEP_MS, Math.max(MIN_SWEEP_MS, (distancePx / SCROLL_SPEED_PX_PER_S) * 1000));

export const overflowDistancePx = (
  root: HTMLElement | null,
  textNode: HTMLElement | null,
): number => {
  if (!root || !textNode) {
    return 0;
  }
  const range = document.createRange();
  range.selectNodeContents(textNode);
  const textWidth = range.getBoundingClientRect().width;
  const style = getComputedStyle(root);
  const horizontalInsets = [
    style.paddingLeft,
    style.paddingRight,
    style.borderLeftWidth,
    style.borderRightWidth,
  ].reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
  const contentWidth = root.getBoundingClientRect().width - horizontalInsets;
  const overflow = textWidth - contentWidth;
  return overflow < SUB_PIXEL_NOISE_PX ? 0 : overflow;
};

const FADE = `${EDGE_FADE_PX}px`;
const MASKS = {
  none: "none",
  left: `linear-gradient(to right, transparent 0, black ${FADE}, black 100%)`,
  right: `linear-gradient(to right, black 0, black calc(100% - ${FADE}), transparent 100%)`,
  both: `linear-gradient(to right, transparent 0, black ${FADE}, black calc(100% - ${FADE}), transparent 100%)`,
};

export const maskAt = (offsetPx: number, overflowPx: number): string => {
  const clippedLeft = offsetPx < -EDGE_FADE_PX / 2;
  const clippedRight = offsetPx > -(overflowPx + EDGE_FADE_PX / 2);
  if (clippedLeft && clippedRight) return MASKS.both;
  if (clippedLeft) return MASKS.left;
  if (clippedRight) return MASKS.right;
  return MASKS.none;
};

export type MaskKeyframe = { offset: number; maskImage: string; webkitMaskImage: string };

export const maskKeyframes = (fromPx: number, toPx: number, overflowPx: number): MaskKeyframe[] => {
  const span = toPx - fromPx;
  const crossings = [-EDGE_FADE_PX / 2, -(overflowPx + EDGE_FADE_PX / 2)]
    .map((threshold) => (span === 0 ? -1 : (threshold - fromPx) / span))
    .filter((t) => t > 0 && t < 1)
    .sort((a, b) => a - b);
  const bounds = [0, ...crossings, 1];
  const frames: MaskKeyframe[] = [];
  for (let i = 0; i < bounds.length - 1; i += 1) {
    const mid = (bounds[i] + bounds[i + 1]) / 2;
    const mask = maskAt(fromPx + span * mid, overflowPx);
    frames.push({ offset: bounds[i], maskImage: mask, webkitMaskImage: mask });
    frames.push({ offset: bounds[i + 1], maskImage: mask, webkitMaskImage: mask });
  }
  return frames;
};
